import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

const API_BASE = 'https://activity.smartandsafe.com.co/api';

const baseQuery = fetchBaseQuery({
  baseUrl: API_BASE,
  prepareHeaders: (headers) => {
    const token = localStorage.getItem('accessToken');
    if (token) {
      headers.set('X-Authorization', `Bearer ${token}`);
    }
    headers.set('Content-Type', 'application/x-www-form-urlencoded');
    return headers;
  },
});

// ─── Servers API ─────────────────────────────────────────
export const serversApi = createApi({
  reducerPath: 'serversApi',
  baseQuery,
  tagTypes: ['Servers'],
  endpoints: (builder) => ({
    listServers: builder.query({
      query: () => '/list/estadoservidores',
      transformResponse: (res) => res?.estadoservidores || [],
      providesTags: ['Servers'],
    }),
    viewServer: builder.query({
      query: (id) => `/view/estadoservidores/${id}`,
      providesTags: (result, error, id) => [{ type: 'Servers', id }],
    }),
    addServer: builder.mutation({
      query: (body) => ({ url: '/estadoservidores/add', method: 'POST', body: new URLSearchParams(body).toString() }),
      invalidatesTags: ['Servers'],
    }),
    editServer: builder.mutation({
      query: ({ id, ...body }) => ({ url: `/edit/estadoservidores/${id}`, method: 'POST', body: new URLSearchParams(body).toString() }),
      invalidatesTags: ['Servers'],
    }),
    restartServer: builder.mutation({
      query: (id) => ({ url: `/servidor/${id}/reiniciar`, method: 'POST' }),
      invalidatesTags: ['Servers'],
    }),
    markPaid: builder.mutation({
      query: (id) => ({ url: `/servidor/${id}/pagado`, method: 'POST' }),
      invalidatesTags: ['Servers'],
    }),
    resolveIssue: builder.mutation({
      query: (id) => ({ url: `/servidor/${id}/problema-resuelto`, method: 'POST' }),
      invalidatesTags: ['Servers'],
    }),
  }),
});

// ─── Clients API ─────────────────────────────────────────
export const clientsApi = createApi({
  reducerPath: 'clientsApi',
  baseQuery,
  tagTypes: ['Clients'],
  endpoints: (builder) => ({
    listClients: builder.query({
      query: () => '/list/clientes',
      transformResponse: (res) => res?.clientes || [],
      providesTags: ['Clients'],
    }),
    addClient: builder.mutation({
      query: (body) => ({ url: '/add/clientes', method: 'POST', body: new URLSearchParams(body).toString() }),
      invalidatesTags: ['Clients'],
    }),
    editClient: builder.mutation({
      query: ({ id, ...body }) => ({ url: `/edit/clientes/${id}`, method: 'POST', body: new URLSearchParams(body).toString() }),
      invalidatesTags: ['Clients'],
    }),
    deleteClient: builder.mutation({
      query: (id) => ({ url: `/delete/clientes/${id}`, method: 'GET' }),
      invalidatesTags: ['Clients'],
    }),
  }),
});

// ─── Projects API ────────────────────────────────────────
export const projectsApi = createApi({
  reducerPath: 'projectsApi',
  baseQuery,
  tagTypes: ['Projects'],
  endpoints: (builder) => ({
    listProjects: builder.query({
      query: () => '/list/proyectos',
      transformResponse: (res) => res?.proyectos || [],
      providesTags: ['Projects'],
    }),
    addProject: builder.mutation({
      query: (body) => ({ url: '/add/proyectos', method: 'POST', body: new URLSearchParams(body).toString() }),
      invalidatesTags: ['Projects'],
    }),
    editProject: builder.mutation({
      query: ({ id, ...body }) => ({ url: `/edit/proyectos/${id}`, method: 'POST', body: new URLSearchParams(body).toString() }),
      invalidatesTags: ['Projects'],
    }),
    deleteProject: builder.mutation({
      query: (id) => ({ url: `/delete/proyectos/${id}`, method: 'GET' }),
      invalidatesTags: ['Projects'],
    }),
  }),
});

// ─── Services API ────────────────────────────────────────
export const servicesApi = createApi({
  reducerPath: 'servicesApi',
  baseQuery,
  tagTypes: ['Services'],
  endpoints: (builder) => ({
    listServices: builder.query({
      query: () => '/list/servicios',
      transformResponse: (res) => res?.servicios || [],
      providesTags: ['Services'],
    }),
    addService: builder.mutation({
      query: (body) => ({ url: '/add/servicios', method: 'POST', body: new URLSearchParams(body).toString() }),
      invalidatesTags: ['Services'],
    }),
    editService: builder.mutation({
      query: ({ id_servicio, ...body }) => ({ url: `/edit/servicios/${id_servicio}`, method: 'POST', body: new URLSearchParams(body).toString() }),
      invalidatesTags: ['Services'],
    }),
    deleteService: builder.mutation({
      query: (id) => ({ url: `/delete/servicios/${id}`, method: 'GET' }),
      invalidatesTags: ['Services'],
    }),
  }),
});

// ─── Metrics API ─────────────────────────────────────────
const metricsBaseQuery = fetchBaseQuery({
  baseUrl: 'https://activity.smartandsafe.com.co',
  prepareHeaders: (headers) => {
    const token = localStorage.getItem('accessToken');
    if (token) {
      headers.set('X-Authorization', `Bearer ${token}`);
    }
    return headers;
  },
});

export const metricsApiSlice = createApi({
  reducerPath: 'metricsApi',
  baseQuery: metricsBaseQuery,
  endpoints: (builder) => ({
    cpuHistory: builder.query({
      query: ({ dias, id_servidor }) => `/historial-cpu.php?dias=${dias}&id_servidor=${id_servidor}`,
    }),
    ramHistory: builder.query({
      query: ({ dias, id_servidor }) => `/historial-ram.php?dias=${dias}&id_servidor=${id_servidor}`,
    }),
    diskHistory: builder.query({
      query: ({ dias, id_servidor }) => `/historial-disco-io.php?dias=${dias}&id_servidor=${id_servidor}`,
    }),
    networkHistory: builder.query({
      query: ({ dias, id_servidor }) => `/historial-red.php?dias=${dias}&id_servidor=${id_servidor}`,
    }),
    loadHistory: builder.query({
      query: ({ dias, agrupacion, id_servidor }) =>
        `/historial-carga.php?dias=${dias}&agrupacion=${agrupacion}${id_servidor ? `&id_servidor=${id_servidor}` : ''}`,
    }),
    peakHours: builder.query({
      query: ({ dias, id_servidor }) =>
        `/horas-pico-carga.php?dias=${dias}${id_servidor ? `&id_servidor=${id_servidor}` : ''}`,
    }),
  }),
});

// ─── MySQL Monitor API ────────────────────────────────────
export const mysqlApi = createApi({
  reducerPath: 'mysqlApi',
  baseQuery,
  tagTypes: ['MysqlServers'],
  endpoints: (builder) => ({
    mysqlServers: builder.query({
      query: () => '/mysql/servers',
      providesTags: ['MysqlServers'],
    }),
    mysqlStatus: builder.query({
      query: (serverId) => `/mysql/status${serverId ? `?server_id=${serverId}` : ''}`,
    }),
    mysqlProcesslist: builder.query({
      query: (serverId) => `/mysql/processlist${serverId ? `?server_id=${serverId}` : ''}`,
    }),
    mysqlVariables: builder.query({
      query: (serverId) => `/mysql/variables${serverId ? `?server_id=${serverId}` : ''}`,
    }),
    mysqlReplication: builder.query({
      query: (serverId) => `/mysql/replication${serverId ? `?server_id=${serverId}` : ''}`,
    }),
    killProcess: builder.mutation({
      query: ({ threadId, serverId }) => ({
        url: `/mysql/kill/${threadId}${serverId ? `?server_id=${serverId}` : ''}`,
        method: 'POST',
      }),
    }),
  }),
});

// ─── Proyecto-Servidor API ───────────────────────────────
export const proyectoServidorApi = createApi({
  reducerPath: 'proyectoServidorApi',
  baseQuery,
  tagTypes: ['ProyectoServidor'],
  endpoints: (builder) => ({
    listProyectoServidores: builder.query({
      query: (proyectoId) => `/list/proyecto-servidores${proyectoId ? `?proyecto_id=${proyectoId}` : ''}`,
      transformResponse: (res) => res?.data || [],
      providesTags: ['ProyectoServidor'],
    }),
    addProyectoServidor: builder.mutation({
      query: (body) => ({ url: '/add/proyecto-servidor', method: 'POST', body: new URLSearchParams(body).toString() }),
      invalidatesTags: ['ProyectoServidor'],
    }),
    deleteProyectoServidor: builder.mutation({
      query: (id) => ({ url: `/delete/proyecto-servidor/${id}`, method: 'GET' }),
      invalidatesTags: ['ProyectoServidor'],
    }),
  }),
});

// ─── Roadmap API ─────────────────────────────────────────
export const roadmapApi = createApi({
  reducerPath: 'roadmapApi',
  baseQuery,
  tagTypes: ['Roadmap'],
  endpoints: (builder) => ({
    listRoadmap: builder.query({
      query: (proyectoId) => `/list/roadmap${proyectoId ? `?proyecto_id=${proyectoId}` : ''}`,
      transformResponse: (res) => res?.tareas || [],
      providesTags: ['Roadmap'],
    }),
    addRoadmapTarea: builder.mutation({
      query: (body) => ({ url: '/add/roadmap', method: 'POST', body: JSON.stringify(body), headers: { 'Content-Type': 'application/json' } }),
      invalidatesTags: ['Roadmap'],
    }),
    editRoadmapTarea: builder.mutation({
      query: ({ id, ...body }) => ({ url: `/edit/roadmap/${id}`, method: 'POST', body: JSON.stringify(body), headers: { 'Content-Type': 'application/json' } }),
      invalidatesTags: ['Roadmap'],
    }),
    deleteRoadmapTarea: builder.mutation({
      query: (id) => ({ url: `/delete/roadmap/${id}`, method: 'GET' }),
      invalidatesTags: ['Roadmap'],
    }),
  }),
});

// ─── URL Monitor API ─────────────────────────────────────
export const urlMonitorApi = createApi({
  reducerPath: 'urlMonitorApi',
  baseQuery,
  tagTypes: ['UrlMonitor'],
  endpoints: (builder) => ({
    listUrlMonitors: builder.query({
      query: (proyectoId) => `/list/url-monitors${proyectoId ? `?proyecto_id=${proyectoId}` : ''}`,
      transformResponse: (res) => res?.monitors || [],
      providesTags: ['UrlMonitor'],
    }),
    addUrlMonitor: builder.mutation({
      query: (body) => ({ url: '/add/url-monitor', method: 'POST', body: JSON.stringify(body), headers: { 'Content-Type': 'application/json' } }),
      invalidatesTags: ['UrlMonitor'],
    }),
    editUrlMonitor: builder.mutation({
      query: ({ id, ...body }) => ({ url: `/edit/url-monitor/${id}`, method: 'POST', body: JSON.stringify(body), headers: { 'Content-Type': 'application/json' } }),
      invalidatesTags: ['UrlMonitor'],
    }),
    deleteUrlMonitor: builder.mutation({
      query: (id) => ({ url: `/delete/url-monitor/${id}`, method: 'GET' }),
      invalidatesTags: ['UrlMonitor'],
    }),
    checkAllUrls: builder.mutation({
      query: () => ({ url: '/url-monitor/check-all', method: 'POST' }),
      invalidatesTags: ['UrlMonitor'],
    }),
    checkSingleUrl: builder.mutation({
      query: (id) => ({ url: `/url-monitor/check/${id}`, method: 'POST' }),
      invalidatesTags: ['UrlMonitor'],
    }),
    urlMonitorLog: builder.query({
      query: ({ monitorId, limit }) => `/url-monitor/log/${monitorId}?limit=${limit || 100}`,
      transformResponse: (res) => res?.log || [],
    }),
  }),
});

// ─── SSL Monitor API ─────────────────────────────────────
export const sslMonitorApi = createApi({
  reducerPath: 'sslMonitorApi',
  baseQuery,
  tagTypes: ['SslMonitor'],
  endpoints: (builder) => ({
    listSslMonitors: builder.query({
      query: (proyectoId) => `/list/ssl-monitors${proyectoId ? `?proyecto_id=${proyectoId}` : ''}`,
      transformResponse: (res) => res?.monitors || [],
      providesTags: ['SslMonitor'],
    }),
    addSslMonitor: builder.mutation({
      query: (body) => ({ url: '/add/ssl-monitor', method: 'POST', body: JSON.stringify(body), headers: { 'Content-Type': 'application/json' } }),
      invalidatesTags: ['SslMonitor'],
    }),
    editSslMonitor: builder.mutation({
      query: ({ id, ...body }) => ({ url: `/edit/ssl-monitor/${id}`, method: 'POST', body: JSON.stringify(body), headers: { 'Content-Type': 'application/json' } }),
      invalidatesTags: ['SslMonitor'],
    }),
    deleteSslMonitor: builder.mutation({
      query: (id) => ({ url: `/delete/ssl-monitor/${id}`, method: 'GET' }),
      invalidatesTags: ['SslMonitor'],
    }),
    checkAllSsl: builder.mutation({
      query: () => ({ url: '/ssl-monitor/check-all', method: 'POST' }),
      invalidatesTags: ['SslMonitor'],
    }),
    checkSingleSsl: builder.mutation({
      query: (id) => ({ url: `/ssl-monitor/check/${id}`, method: 'POST' }),
      invalidatesTags: ['SslMonitor'],
    }),
  }),
});

// ─── Dominios (Vencimiento) API ──────────────────────────
export const dominiosApi = createApi({
  reducerPath: 'dominiosApi',
  baseQuery,
  tagTypes: ['Dominios'],
  endpoints: (builder) => ({
    listDominios: builder.query({
      query: () => '/list/dominios',
      transformResponse: (res) => res?.dominios || [],
      providesTags: ['Dominios'],
    }),
    addDominio: builder.mutation({
      query: (body) => ({ url: '/add/dominio', method: 'POST', body: JSON.stringify(body), headers: { 'Content-Type': 'application/json' } }),
      invalidatesTags: ['Dominios'],
    }),
    editDominio: builder.mutation({
      query: ({ id, ...body }) => ({ url: `/edit/dominio/${id}`, method: 'POST', body: JSON.stringify(body), headers: { 'Content-Type': 'application/json' } }),
      invalidatesTags: ['Dominios'],
    }),
    deleteDominio: builder.mutation({
      query: (id) => ({ url: `/delete/dominio/${id}`, method: 'GET' }),
      invalidatesTags: ['Dominios'],
    }),
    checkAllDominios: builder.mutation({
      query: () => ({ url: '/dominios/check-all', method: 'POST' }),
      invalidatesTags: ['Dominios'],
    }),
    checkDominio: builder.mutation({
      query: (id) => ({ url: `/dominios/check/${id}`, method: 'POST' }),
      invalidatesTags: ['Dominios'],
    }),
  }),
});

// ─── Alertas API ─────────────────────────────────────────
export const alertasApi = createApi({
  reducerPath: 'alertasApi',
  baseQuery,
  tagTypes: ['Alertas'],
  endpoints: (builder) => ({
    listAlertas: builder.query({
      query: () => '/list/alertas',
      transformResponse: (res) => res?.alertas || [],
      providesTags: ['Alertas'],
    }),
    addAlerta: builder.mutation({
      query: (body) => ({ url: '/add/alerta', method: 'POST', body: JSON.stringify(body), headers: { 'Content-Type': 'application/json' } }),
      invalidatesTags: ['Alertas'],
    }),
    editAlerta: builder.mutation({
      query: ({ id, ...body }) => ({ url: `/edit/alerta/${id}`, method: 'POST', body: JSON.stringify(body), headers: { 'Content-Type': 'application/json' } }),
      invalidatesTags: ['Alertas'],
    }),
    deleteAlerta: builder.mutation({
      query: (id) => ({ url: `/delete/alerta/${id}`, method: 'GET' }),
      invalidatesTags: ['Alertas'],
    }),
    listAlertasLog: builder.query({
      query: (limit) => `/list/alertas-log?limit=${limit || 100}`,
      transformResponse: (res) => res?.log || [],
      providesTags: ['AlertasLog'],
    }),
    evaluarAlertas: builder.mutation({
      query: () => ({ url: '/alertas/evaluar', method: 'POST' }),
      invalidatesTags: ['Alertas', 'AlertasLog'],
    }),
  }),
});

// ─── Bitácora API ────────────────────────────────────────
export const bitacoraApi = createApi({
  reducerPath: 'bitacoraApi',
  baseQuery,
  tagTypes: ['Bitacora'],
  endpoints: (builder) => ({
    listBitacora: builder.query({
      query: (params) => {
        const qs = new URLSearchParams();
        if (params?.servidor_id) qs.set('servidor_id', params.servidor_id);
        if (params?.proyecto_id) qs.set('proyecto_id', params.proyecto_id);
        if (params?.limit) qs.set('limit', params.limit);
        const str = qs.toString();
        return `/list/bitacora${str ? `?${str}` : ''}`;
      },
      transformResponse: (res) => res?.bitacora || [],
      providesTags: ['Bitacora'],
    }),
    addBitacora: builder.mutation({
      query: (body) => ({ url: '/add/bitacora', method: 'POST', body: JSON.stringify(body), headers: { 'Content-Type': 'application/json' } }),
      invalidatesTags: ['Bitacora'],
    }),
    deleteBitacora: builder.mutation({
      query: (id) => ({ url: `/delete/bitacora/${id}`, method: 'GET' }),
      invalidatesTags: ['Bitacora'],
    }),
  }),
});

// ─── Reportes API ────────────────────────────────────────
export const reportesApi = createApi({
  reducerPath: 'reportesApi',
  baseQuery,
  tagTypes: ['Reportes'],
  endpoints: (builder) => ({
    listReportes: builder.query({
      query: (proyectoId) => `/list/reportes${proyectoId ? `?proyecto_id=${proyectoId}` : ''}`,
      transformResponse: (res) => res?.reportes || [],
      providesTags: ['Reportes'],
    }),
    addReporte: builder.mutation({
      query: (body) => ({ url: '/add/reporte', method: 'POST', body: JSON.stringify(body), headers: { 'Content-Type': 'application/json' } }),
      invalidatesTags: ['Reportes'],
    }),
    viewReporte: builder.query({
      query: (id) => `/view/reporte/${id}`,
      transformResponse: (res) => res?.reporte || null,
    }),
    deleteReporte: builder.mutation({
      query: (id) => ({ url: `/delete/reporte/${id}`, method: 'GET' }),
      invalidatesTags: ['Reportes'],
    }),
    generarReporteMensual: builder.mutation({
      query: (body) => ({ url: '/reporte/generar-mensual', method: 'POST', body: JSON.stringify(body), headers: { 'Content-Type': 'application/json' } }),
      invalidatesTags: ['Reportes'],
    }),
    generarReportesRecomendados: builder.mutation({
      query: (body) => ({ url: '/reportes/generar-recomendados', method: 'POST', body: JSON.stringify(body || {}), headers: { 'Content-Type': 'application/json' } }),
      invalidatesTags: ['Reportes'],
    }),
  }),
});

// ─── Proyecto Detalle API ────────────────────────────────
export const proyectoDetalleApi = createApi({
  reducerPath: 'proyectoDetalleApi',
  baseQuery,
  tagTypes: ['ProyectoDetalle'],
  endpoints: (builder) => ({
    viewProyectoDetalle: builder.query({
      query: (id) => `/view/proyecto/${id}`,
      transformResponse: (res) => res?.proyecto || null,
      providesTags: (result, error, id) => [{ type: 'ProyectoDetalle', id }],
    }),
  }),
});

// ─── Export Hooks ────────────────────────────────────────
export const {
  useListServersQuery,
  useViewServerQuery,
  useAddServerMutation,
  useEditServerMutation,
  useRestartServerMutation,
  useMarkPaidMutation,
  useResolveIssueMutation,
} = serversApi;

export const {
  useListClientsQuery,
  useAddClientMutation,
  useEditClientMutation,
  useDeleteClientMutation,
} = clientsApi;

export const {
  useListProjectsQuery,
  useAddProjectMutation,
  useEditProjectMutation,
  useDeleteProjectMutation,
} = projectsApi;

export const {
  useListServicesQuery,
  useAddServiceMutation,
  useEditServiceMutation,
  useDeleteServiceMutation,
} = servicesApi;

export const {
  useCpuHistoryQuery,
  useRamHistoryQuery,
  useDiskHistoryQuery,
  useNetworkHistoryQuery,
  useLoadHistoryQuery,
  usePeakHoursQuery,
} = metricsApiSlice;

export const {
  useMysqlServersQuery,
  useMysqlStatusQuery,
  useMysqlProcesslistQuery,
  useMysqlVariablesQuery,
  useMysqlReplicationQuery,
  useKillProcessMutation,
} = mysqlApi;

export const {
  useListProyectoServidoresQuery,
  useAddProyectoServidorMutation,
  useDeleteProyectoServidorMutation,
} = proyectoServidorApi;

export const {
  useListRoadmapQuery,
  useAddRoadmapTareaMutation,
  useEditRoadmapTareaMutation,
  useDeleteRoadmapTareaMutation,
} = roadmapApi;

export const {
  useListUrlMonitorsQuery,
  useAddUrlMonitorMutation,
  useEditUrlMonitorMutation,
  useDeleteUrlMonitorMutation,
  useCheckAllUrlsMutation,
  useCheckSingleUrlMutation,
  useUrlMonitorLogQuery,
} = urlMonitorApi;

export const {
  useListSslMonitorsQuery,
  useAddSslMonitorMutation,
  useEditSslMonitorMutation,
  useDeleteSslMonitorMutation,
  useCheckAllSslMutation,
  useCheckSingleSslMutation,
} = sslMonitorApi;

export const {
  useListDominiosQuery,
  useAddDominioMutation,
  useEditDominioMutation,
  useDeleteDominioMutation,
  useCheckAllDominiosMutation,
  useCheckDominioMutation,
} = dominiosApi;

export const {
  useListAlertasQuery,
  useAddAlertaMutation,
  useEditAlertaMutation,
  useDeleteAlertaMutation,
  useListAlertasLogQuery,
  useEvaluarAlertasMutation,
} = alertasApi;

export const {
  useListBitacoraQuery,
  useAddBitacoraMutation,
  useDeleteBitacoraMutation,
} = bitacoraApi;

export const {
  useListReportesQuery,
  useAddReporteMutation,
  useViewReporteQuery,
  useDeleteReporteMutation,
  useGenerarReporteMensualMutation,
  useGenerarReportesRecomendadosMutation,
} = reportesApi;

export const {
  useViewProyectoDetalleQuery,
} = proyectoDetalleApi;

// ─── Ollama / IA SMART API ───────────────────────────────
export const ollamaApi = createApi({
  reducerPath: 'ollamaApi',
  baseQuery,
  tagTypes: ['OllamaStats', 'OllamaConfig'],
  endpoints: (builder) => ({
    ollamaServers: builder.query({
      query: () => '/ollama/servers',
    }),
    ollamaStatus: builder.query({
      query: (serverId) => `/ollama/status${serverId ? `?server_id=${serverId}` : ''}`,
    }),
    ollamaStats: builder.query({
      query: () => '/ollama/stats',
      providesTags: ['OllamaStats'],
    }),
    ollamaConfig: builder.query({
      query: () => '/ollama/config',
      providesTags: ['OllamaConfig'],
    }),
    updateOllamaConfig: builder.mutation({
      query: (body) => ({
        url: '/ollama/config',
        method: 'POST',
        body: JSON.stringify(body),
        headers: { 'Content-Type': 'application/json' },
      }),
      invalidatesTags: ['OllamaConfig'],
    }),
    pullOllamaModel: builder.mutation({
      query: (body) => ({
        url: '/ollama/model/pull',
        method: 'POST',
        body: JSON.stringify(body),
        headers: { 'Content-Type': 'application/json' },
      }),
    }),
    loadOllamaModel: builder.mutation({
      query: (body) => ({
        url: '/ollama/model/load',
        method: 'POST',
        body: JSON.stringify(body),
        headers: { 'Content-Type': 'application/json' },
      }),
    }),
    logOllamaChat: builder.mutation({
      query: (body) => ({
        url: '/ollama/log-chat',
        method: 'POST',
        body: JSON.stringify(body),
        headers: { 'Content-Type': 'application/json' },
      }),
      invalidatesTags: ['OllamaStats'],
    }),
  }),
});

export const {
  useOllamaServersQuery,
  useOllamaStatusQuery,
  useOllamaStatsQuery,
  useOllamaConfigQuery,
  useUpdateOllamaConfigMutation,
  usePullOllamaModelMutation,
  useLoadOllamaModelMutation,
  useLogOllamaChatMutation,
} = ollamaApi;

// ─── Server Processes API ────────────────────────────────
export const serverProcessesApi = createApi({
  reducerPath: 'serverProcessesApi',
  baseQuery,
  tagTypes: ['ServerProcesses'],
  endpoints: (builder) => ({
    listServerProcesses: builder.query({
      query: (params = {}) => {
        const searchParams = new URLSearchParams();
        Object.entries(params).forEach(([k, v]) => {
          if (v !== undefined && v !== null && v !== '') searchParams.set(k, v);
        });
        return `/list/server-processes?${searchParams.toString()}`;
      },
      providesTags: ['ServerProcesses'],
    }),
  }),
});

export const { useListServerProcessesQuery } = serverProcessesApi;

// ─── DNS Status API (Cloudflare) ─────────────────────────
export const dnsStatusApi = createApi({
  reducerPath: 'dnsStatusApi',
  baseQuery,
  tagTypes: ['DnsStatus'],
  endpoints: (builder) => ({
    getDnsStatus: builder.query({
      query: () => '/dns/status',
      transformResponse: (res) => res?.dns_status || {},
      providesTags: ['DnsStatus'],
    }),
  }),
});

export const { useGetDnsStatusQuery } = dnsStatusApi;
